import * as olExtent from 'ol/extent';

import Select from 'ol/interaction/Select';
import { always, singleClick } from 'ol/events/condition';

//abstact MapElement Class
export default class MapElement {
    constructor(map, id, mapElementSettings = { clickmethod: 'browser', interactable: false, group: false, defaultOn: false, actions: false }, parent) {
        if (this.constructor == MapElement) {
            throw new Error("Abstract classes can't be instantiated.");
        }

        this.id = id;
        this.mapElements = [];
        this.layer = null;
        this.subLayers = null;

        this.mapModel = map;
        this.parent = parent;

        this.onSelectEvent = null;
        this.selectInteraction = null;

        this.mapElementSettings = mapElementSettings;
        this.visible = true;
        this.disabled = false;
    }

    initializeSelect() {
        if (this.selectInteraction) {
            this.removeSelect()
        }

        this.selectInteraction = new Select(
            {
                condition: function (evt) {
                    if (this.mapElementSettings.clickmethod == 'browser')
                        return singleClick(evt);    // for civil
                    return always(evt);       // for tablet
                }.bind(this),
                layers: (layer) => {
                    if (this.mapElements.length > 0) {
                        return this.mapElements.some(element => 
                            { 
                                return element.layer.get('parentId') == layer.get('parentId') 
                            });
                    } else {
                        // only allow layers from this module
                        if (!this.layer) return false;
                        if (!layer) return false;
                        if (layer.ol_uid == this.layer.ol_uid) {
                            return true;
                        }
                        if (this.layer.get("layers"))
                        {
                           return this.layer.get("layers").getArray().some(e => e.ol_uid == layer.ol_uid)
                        }
                        return false;
                    }

                }
            });
        this.selectInteraction.setHitTolerance(10);
        this.mapModel.map.addInteraction(this.selectInteraction);

        this.selectInteraction.on('select', (selectEvent) => {
            //console.log(selectEvent);
            if (this.onSelectEvent) this.onSelectEvent(selectEvent);
        });
    }

    removeSelect() {
        this.mapModel.map.removeInteraction(this.selectInteraction);
    }

    addSelectEventFunction(funct) {
        this.onSelectEvent = funct;
    }

    setSelectEventFunctionState(state) {
        this.selectInteraction.setActive(state);
    }

    getChildElement(id) {
        return this.mapElements.find(element => element.id === id);
    }

    getLayers() {
        var layerObject;
        var layerArray = [];

        if (!this.layer) {
            return null;
        }
        if (!this.mapElementSettings.interactable) {
            if (this.group) {
                return null;
            } else {
                this.mapElements.forEach(mapelement => {
                    if (mapelement.getLayers()) {
                        layerArray.push(mapelement.getLayers());
                    }
                });
                return layerArray;
            }
        }
        if (this.mapElementSettings.group) { // only actions if no child elements?
            layerObject = { id: this.parent.id + "-" + this.id, name: this.id, editButton: this.mapElementSettings.editButton, actions: this.mapElementSettings.actions, checkBoxValue: false, visible: this.visible, disabled: this.disabled, show: this.show.bind(this), hide: this.hide.bind(this), children: [] };
            if (!this.noKey) layerObject.key = this.id;
            return layerObject
        }
        else {
            layerObject = { id: this.id, name: this.id, editButton: this.mapElementSettings.editButton, actions: this.mapElementSettings.actions, checkBoxValue: false, visible: this.visible, disabled: this.disabled, show: this.show.bind(this), hide: this.hide.bind(this), children: [] };
            if (!this.noKey) layerObject.key = this.id;

            this.mapElements.forEach(mapelement => {
                if (mapelement.getLayers()) {
                    layerObject.children.push(mapelement.getLayers());
                }
            });
            return layerObject;
        }
    }

    show() {
        // console.log(`show layer: ${this.id}`);
        this.visible = true;
        // can not show if its not grouped, it will loop back on itself when show() is used on child, to show all children if visible is false 
        // use showChildren if you want to make all children visible
        if (this.mapElementSettings.group) {
            if (this.mapElements.length > 0) {
                this.mapElements.forEach(mapElement => {
                    mapElement.show();
                });
            }
        }
        if (this.layer) this.layer.setVisible(true);
        // this.parent.show is not a function on mapModel, so check if function exists
        // check if parent isnt already visible, if it is dont do show() (or else it will loop infinitly)
        if (this.parent && this.parent.show && !this.parent.visible) this.parent.show()
    }

    hide() {
        //console.log(`hide layer: ${this.id}`);
        this.visible = false;
        if (this.layer) this.layer.setVisible(false);

        if (this.mapElements.length > 0) {
            this.mapElements.forEach(mapElement => {
                mapElement.hide();
            });
        }
    }

    showChildren() {
        // no need to check if visible, it will do it on its own if this layer is not visible by showing from the child element. 
        // exmpl: MOTIONPLANCOLLECTION.visible: false
        // motionplanChild.show() -> check if parent visible -> MOTIONPLANCOLLECTION.show()
        if (this.mapElements.length > 0) {
            this.mapElements.forEach(mapElement => {
                mapElement.showChildren();
            });
        } else {
            this.show()
        }
    }

    removeLayer() {
        this.mapModel.map.removeLayer(this.layer);
        //this.parent.layer.removeLayer(this.layer);
    }

    clear() {
        if (this.mapElements.length > 0) {
            this.mapElements.forEach(element => {
                this.visible = true;
                element.clear();
            })
        } else {
            if (this.parent.layer) {
                this.parent.layer.getLayers().forEach((value, index) => {
                    if (this.layer && value && value.ol_uid === this.layer.ol_uid) this.parent.layer.getLayers().removeAt(index);
                })
                this.layer = null;
                this.visible = true;
            } else {
                // no parent layer?
                this.layer?.getSource()?.clear()
                this.layer = null
            }
        }
    }

    getExtent() {
        var extent = olExtent.createEmpty();
        if (this.mapElements.length > 0) {
            this.mapElements.forEach(element => {
                if (element.getExtent()) {
                    olExtent.extend(extent, element.getExtent());
                }
            });
        } else if (this.layer) {
            try {
                extent = this.layer.getSource()?.getExtent();
            } catch (error) {
                console.error(error)
                console.error(this)
            }
        }
        return extent
    }

    unselectAll() {
        //there might be situations where this function is called on an element without a select interaction
        this.selectInteraction?.getFeatures().clear();
    }
}